/**
 * @fileoverview The pickup page's power bank block for one client: the only
 * place a city manager records that a power bank was handed to a BA.
 *
 * It works exactly like the flyer codes above it: drag a serial from City
 * inventory onto a brand ambassador to hand it over, drag it back to City
 * inventory when it is returned, or click a serial with exactly one BA
 * selected in the sidebar. The drag mechanics (grab on mouse move, the
 * chip that follows the cursor, the drop zones) are the flyer widget's own
 * components, reused rather than copied.
 *
 * The one difference from flyers: a power bank is handed over against a
 * hold, so the drop opens a dialog - "place a hold? how much?" with the
 * city's default already filled in and editable - and the unit only changes
 * column once the API accepts it. Under each unit that is out, the hold
 * amount and whether the BA has agreed to it from the app.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import classNames from 'classnames'

import DropZone from 'Components/DropZone'
import { SnackBarContext } from 'Components/SnackBar'
import PowerBankDialog from 'Components/PowerBanks/PowerBankDialog'
import HoldAmountInput from 'Components/PowerBanks/HoldAmountInput'
import {
  readGraphQLData,
  useCityPowerBankHolds,
} from 'Components/PowerBanks/hooks'
import { usePowerBankCity } from 'Components/PowerBanks/PowerBankCityContext'
import {
  assignPowerBank,
  returnPowerBank,
  writeOffPowerBank,
} from 'Module/graphQL'
import {
  describeHoldLine,
  formatHoldAmount,
  parseHoldAmountInput,
  sortUnitsBySerial,
} from 'Utils/PowerBanks'
import {
  INVENTORY_MOVEMENT_DISTRIBUTED,
  INVENTORY_MOVEMENT_IN,
  INVENTORY_MOVEMENT_OUT,
} from 'shared/constants'

import CodeBlocksContainer from '../FlyerCodesGiveOutWidget/CodeBlocksContainer'
import CodeDragger from '../FlyerCodesGiveOutWidget/CodeDragger'
import groupByBA from '../FlyerCodesGiveOutWidget/groupByBA'

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 10px;

  .CodeBlock {
    position: absolute;
    cursor: pointer;
  }

  .CodeBlocksContainer {
    display: inline-block;
    vertical-align: top;
    position: relative;
    width: 100%;
  }

  .AvailableCodesContainer,
  .GiveOutContainer {
    width: 50%;
    display: inline-block;
    vertical-align: top;
  }

  .header {
    background-color: white;
    padding: 10px 10px 10px 0;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #7e8fa5;
    font-size: 11px;
    font-weight: bold;
  }

  .content {
    padding: 10px;
    max-height: 350px;
    overflow-x: auto;
    border: 1px solid #eceef0;
    min-height: 112px;
  }

  .city-content {
    margin-right: 20px;
  }

  .city-content .CodeBlocksContainer {
    padding-bottom: 20px;
  }

  .BADropZoneNotActive,
  .BADropZone {
    padding: 10px;
    box-sizing: border-box;
    border: 1px solid #eceef0;
    margin-bottom: 10px;
  }

  .AvailableCodesContainer > .city-content > .BADropZoneNotActive,
  .AvailableCodesContainer > .city-content > .BADropZone {
    padding: 0;
    border: 0;
  }

  .BADropZone:hover {
    background-color: #f7f4f4;
    border: 2px dashed #afa9a9;
  }

  .BADropZone:hover > .CodeBlocksContainer {
    opacity: 0.5;
  }

  h3 {
    color: #7e8fa5;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 1.5px;
    margin: 0 0 10px 0;
  }

  .view-summary {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 20px;
    color: #9f9f9f;
    font-size: 12px;
  }

  .empty,
  .hint {
    color: #9f9f9f;
    font-size: 12px;
  }

  .hint {
    margin-top: 6px;
  }

  .hold-lines {
    margin-top: 6px;
  }

  .hold-line {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #011533;
    padding: 2px 0;
  }

  .hold-line .serial {
    font-family: monospace;
    color: #7e8fa5;
  }

  .hold-line .agreed {
    color: #1e8e3e;
  }

  .hold-line .awaiting {
    color: #c77700;
  }

  .hold-line .lost {
    margin-left: auto;
    background: none;
    border: 0;
    padding: 0;
    color: #c8102e;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
  }

  .setting-error {
    color: #c8102e;
    font-size: 12px;
    margin-top: 6px;
  }
`

const HOLD_QUESTION = 'Do you want to place a hold on this brand ambassador?'

const STEP_NONE = null
const STEP_HANDOVER = 'handover'
const STEP_RETURN = 'return'
const STEP_WRITE_OFF = 'writeOff'

const unitLabel = unit => unit.description || `Power bank #${unit.inventoryId}`

/**
 * A unit as the flyer drag components expect it: `code` is what the chip
 * shows and `uniqueKey` is what React keys on.
 */
const toChip = unit => ({
  ...unit,
  code: unitLabel(unit),
  uniqueKey: String(unit.inventoryId),
  ambassadorId: unit.ambassadorId === undefined ? null : unit.ambassadorId,
})

const PowerBanksSection = ({
  cityId,
  availableUnits,
  givenOutUnits,
  ambassadors,
  selectedBrandAmbassadors,
  updateLocalPowerBankState,
}) => {
  const runSnackBar = useContext(SnackBarContext)
  const {
    money,
    setting,
    loading: cityLoading,
    settingError,
  } = usePowerBankCity()
  const { holdsByInventoryId, reload: reloadHolds } = useCityPowerBankHolds(
    cityId,
  )
  const hasDefaultHold = setting.holdRequired && Number(setting.holdAmount) > 0

  const availableChips = useMemo(
    () => sortUnitsBySerial(availableUnits).map(toChip),
    [availableUnits],
  )
  const givenOutChips = useMemo(
    () => sortUnitsBySerial(givenOutUnits).map(toChip),
    [givenOutUnits],
  )

  // The unit currently being dragged, exactly like the flyer widget's
  // codeInAction. Cleared on any mouse up.
  const [unitInAction, setUnitInAction] = useState(null)
  useEffect(() => {
    const clear = () => setUnitInAction(null)
    document.addEventListener('mouseup', clear)
    document.addEventListener('touchup', clear)
    return () => {
      document.removeEventListener('mouseup', clear)
      document.removeEventListener('touchup', clear)
    }
  }, [])

  const [step, setStep] = useState(STEP_NONE)
  const [dialogUnit, setDialogUnit] = useState(null)
  const [dialogAmbassador, setDialogAmbassador] = useState(null)
  const [amountText, setAmountText] = useState('')
  const [amountError, setAmountError] = useState(null)

  const closeDialog = useCallback(() => {
    setStep(STEP_NONE)
    setDialogUnit(null)
    setDialogAmbassador(null)
    setAmountError(null)
  }, [])

  const findAmbassador = useCallback(
    (ambassadorId, fallbackName) =>
      ambassadors.find(ba => ba.id == ambassadorId) || {
        id: ambassadorId,
        name: fallbackName || 'Unknown BA',
      },
    [ambassadors],
  )

  const openHandover = useCallback(
    (unit, ambassador) => {
      if (!unit || !ambassador || cityLoading || settingError) return
      setDialogUnit(unit)
      setDialogAmbassador(ambassador)
      setAmountText(hasDefaultHold ? String(setting.holdAmount) : '')
      setAmountError(null)
      setStep(STEP_HANDOVER)
    },
    [cityLoading, settingError, hasDefaultHold, setting],
  )

  const openReturn = useCallback(
    unit => {
      if (!unit || !unit.ambassadorId) return
      setDialogUnit(unit)
      setDialogAmbassador(
        findAmbassador(unit.ambassadorId, unit.ambassadorName),
      )
      setStep(STEP_RETURN)
    },
    [findAmbassador],
  )

  const openWriteOff = useCallback(
    unit => {
      setDialogUnit(unit)
      setDialogAmbassador(
        findAmbassador(unit.ambassadorId, unit.ambassadorName),
      )
      setStep(STEP_WRITE_OFF)
    },
    [findAmbassador],
  )

  // ---- drag and drop, mirroring FlyerCodesGiveOutWidget ----

  const onGrab = useCallback(unit => setUnitInAction(unit), [])

  /** Dropped on a BA: hand over (from stock) or move between BAs is refused. */
  const onDropOnAmbassador = useCallback(
    (ambassador, unit) => {
      setUnitInAction(null)
      if (!unit) return
      if (unit.ambassadorId) {
        if (String(unit.ambassadorId) === String(ambassador.id)) return
        runSnackBar({
          type: 'error',
          msg: `${unitLabel(
            unit,
          )} must be returned to city inventory before it can go to ${
            ambassador.name
          }`,
        })
        return
      }
      openHandover(unit, ambassador)
    },
    [openHandover, runSnackBar],
  )

  /** Dropped back on city inventory: a return. */
  const onDropOnCity = useCallback(
    unit => {
      setUnitInAction(null)
      if (!unit || !unit.ambassadorId) return
      openReturn(unit)
    },
    [openReturn],
  )

  const singleSelectedAmbassador =
    selectedBrandAmbassadors && selectedBrandAmbassadors.length === 1
      ? selectedBrandAmbassadors[0]
      : null

  /** Click on a stock chip with one BA selected in the sidebar = hand over. */
  const onCityChipClick = useCallback(
    unit => {
      if (singleSelectedAmbassador) openHandover(unit, singleSelectedAmbassador)
    },
    [singleSelectedAmbassador, openHandover],
  )

  /** Click on a BA's chip = return, the same shortcut flyers have. */
  const onAmbassadorChipClick = useCallback(
    unit => {
      if (singleSelectedAmbassador) openReturn(unit)
    },
    [singleSelectedAmbassador, openReturn],
  )

  // ---- the API calls ----

  /**
   * Sends the handover. `holdAmount` 0 = explicit no hold (the API writes no
   * payroll row), a number = that amount for this one handover. Never send
   * null from here: null means "use the city default" on the API.
   */
  const submitHandover = useCallback(
    async holdAmount => {
      const data = await readGraphQLData(
        await assignPowerBank({
          inventoryId: dialogUnit.inventoryId,
          ambassadorId: dialogAmbassador.id,
          cityId,
          holdAmount,
          comments: 'Handed over on the pickup page.',
        }),
      )
      const payload = data.assignPowerBank
      const hold = payload && payload.hold
      const heldMoney = hold && Number(hold.amount) > 0

      updateLocalPowerBankState({
        type: INVENTORY_MOVEMENT_OUT,
        unit: dialogUnit,
        ambassadorId: dialogAmbassador.id,
        ambassadorName: dialogAmbassador.name,
      })
      closeDialog()
      reloadHolds()
      runSnackBar({
        type: 'success',
        msg: heldMoney
          ? `${unitLabel(dialogUnit)} handed to ${
              dialogAmbassador.name
            }, hold of ${formatHoldAmount(hold.amount, {
              currencySign: money.currencySign,
              currency: hold.currency,
            })} placed. The BA is asked to agree to it in the app.`
          : `${unitLabel(dialogUnit)} handed to ${
              dialogAmbassador.name
            } with no hold`,
      })
    },
    [
      dialogUnit,
      dialogAmbassador,
      cityId,
      updateLocalPowerBankState,
      closeDialog,
      reloadHolds,
      runSnackBar,
      money,
    ],
  )

  /** "Yes": place the hold for the amount in the box (default, editable). */
  const onHoldYes = useCallback(async () => {
    const { value, error } = parseHoldAmountInput(amountText)
    if (error) {
      // Shown under the field; the dialog stays open without a second banner.
      setAmountError(error)
      return
    }
    setAmountError(null)
    await submitHandover(value)
  }, [amountText, submitHandover])

  // "No" is unconditional: always an explicit zero hold. `null` would tell
  // the API to fall back to the city default, which is exactly what the
  // manager just declined.
  const onHoldNo = useCallback(() => submitHandover(0), [submitHandover])

  const onReturnConfirm = useCallback(async () => {
    const data = await readGraphQLData(
      await returnPowerBank({
        inventoryId: dialogUnit.inventoryId,
        ambassadorId: dialogAmbassador.id,
        cityId,
        comments: 'Returned on the pickup page.',
      }),
    )
    const hold = data.returnPowerBank && data.returnPowerBank.hold
    const releasedMoney = hold && Number(hold.amount) > 0

    updateLocalPowerBankState({ type: INVENTORY_MOVEMENT_IN, unit: dialogUnit })
    closeDialog()
    reloadHolds()
    runSnackBar({
      type: 'success',
      msg: releasedMoney
        ? `${unitLabel(dialogUnit)} returned, hold of ${formatHoldAmount(
            hold.amount,
            { currencySign: money.currencySign, currency: hold.currency },
          )} released to ${dialogAmbassador.name}`
        : `${unitLabel(dialogUnit)} returned by ${dialogAmbassador.name}`,
    })
  }, [
    dialogUnit,
    dialogAmbassador,
    cityId,
    updateLocalPowerBankState,
    closeDialog,
    reloadHolds,
    runSnackBar,
    money,
  ])

  const onWriteOffConfirm = useCallback(async () => {
    const data = await readGraphQLData(
      await writeOffPowerBank({
        inventoryId: dialogUnit.inventoryId,
        ambassadorId: dialogAmbassador.id,
        cityId,
        comments: 'Reported lost or broken on the pickup page.',
      }),
    )
    const hold = data.writeOffPowerBank && data.writeOffPowerBank.hold
    const keptMoney = hold && Number(hold.amount) > 0

    // Written off: the unit leaves both columns.
    updateLocalPowerBankState({
      type: INVENTORY_MOVEMENT_DISTRIBUTED,
      unit: dialogUnit,
    })
    closeDialog()
    reloadHolds()
    runSnackBar({
      type: 'success',
      msg: keptMoney
        ? `${unitLabel(dialogUnit)} written off, hold of ${formatHoldAmount(
            hold.amount,
            { currencySign: money.currencySign, currency: hold.currency },
          )} kept. ${dialogAmbassador.name} can be handed a new power bank.`
        : `${unitLabel(dialogUnit)} written off. ${
            dialogAmbassador.name
          } can be handed a new power bank.`,
    })
  }, [
    dialogUnit,
    dialogAmbassador,
    cityId,
    updateLocalPowerBankState,
    closeDialog,
    reloadHolds,
    runSnackBar,
    money,
  ])

  // ---- render ----

  const notInAction = chip =>
    !unitInAction || chip.uniqueKey !== unitInAction.uniqueKey

  const groups = groupByBA(givenOutChips.filter(notInAction), ambassadors, true)

  const dropZoneClass = classNames({
    BADropZone: Boolean(unitInAction),
    BADropZoneNotActive: !unitInAction,
  })

  return (
    <div>
      <h4 className="section-title">Power banks</h4>
      <Wrapper>
        <div
          className="AvailableCodesContainer"
          cypress_id="powerBankCityStock"
        >
          <div className="header">City inventory</div>
          <div className="content city-content">
            <DropZone
              onDrop={unitInAction ? () => onDropOnCity(unitInAction) : null}
              className={dropZoneClass}
            >
              {availableChips.length === 0 && !unitInAction ? (
                <div className="empty">No power banks in stock</div>
              ) : (
                <CodeBlocksContainer
                  codes={availableChips.filter(notInAction)}
                  totalCodes={
                    availableChips.length > 25
                      ? availableChips.length
                      : undefined
                  }
                  onCodeGrab={onGrab}
                  onCodeClick={onCityChipClick}
                />
              )}
            </DropZone>
            <div className="hint">
              Drag a serial onto a brand ambassador to hand it over, or click it
              with one ambassador selected.
            </div>
            {settingError && (
              <div className="setting-error" role="alert">
                Could not load this city&apos;s hold setting: {settingError}.
                Reload the page before handing over.
              </div>
            )}
          </div>
        </div>
        <div className="GiveOutContainer" cypress_id="powerBankBAStock">
          <div className="header">User inventory</div>
          <div className="content">
            {groups.length === 0 && (
              <div className="empty">
                Select brand ambassadors in the sidebar to hand over power banks
              </div>
            )}
            {groups.map(group => (
              <DropZone
                key={group.id}
                onDrop={
                  unitInAction
                    ? () => onDropOnAmbassador(group, unitInAction)
                    : null
                }
                className={dropZoneClass}
                cypress_id="powerBankBAList"
              >
                <a
                  href={`/dashboard/cities/${cityId}/ambassadors/${group.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <h3 title={group.title}>{group.name}</h3>
                </a>
                <CodeBlocksContainer
                  codes={group.codes}
                  onCodeGrab={onGrab}
                  onCodeClick={onAmbassadorChipClick}
                />
                {group.codes.length > 0 && (
                  <div className="hold-lines">
                    {group.codes.map(unit => {
                      const { amount, agreement } = describeHoldLine(
                        holdsByInventoryId[String(unit.inventoryId)],
                        money,
                      )
                      return (
                        <div className="hold-line" key={unit.uniqueKey}>
                          <span className="serial">{unitLabel(unit)}</span>
                          <span>{amount}</span>
                          {agreement && (
                            <span className={agreement.className}>
                              {agreement.text}
                            </span>
                          )}
                          <button
                            type="button"
                            className="lost"
                            onClick={() => openWriteOff(unit)}
                          >
                            Lost / broken
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </DropZone>
            ))}
          </div>
        </div>

        {unitInAction ? <CodeDragger code={unitInAction} /> : null}
      </Wrapper>

      {step === STEP_HANDOVER && dialogUnit && (
        <PowerBankDialog
          show
          title="Hand over power bank"
          confirmLabel="Yes, place hold and hand over"
          secondaryLabel="No hold"
          onConfirm={onHoldYes}
          onSecondary={onHoldNo}
          onCancel={closeDialog}
        >
          <p>
            You are handing <strong>{unitLabel(dialogUnit)}</strong> to{' '}
            <strong>{dialogAmbassador.name}</strong>.
          </p>
          <p>
            <strong>{HOLD_QUESTION}</strong>
          </p>
          <p>
            {hasDefaultHold
              ? `This city's default is ${formatHoldAmount(
                  setting.holdAmount,
                  money,
                )}. Change it below if this handover needs a different amount.`
              : 'This city has no default hold. Enter the amount to hold, or choose No hold.'}
          </p>
          <HoldAmountInput
            value={amountText}
            onChange={setAmountText}
            error={amountError}
            money={money}
          />
          <p>
            The amount is deducted from the ambassador&apos;s pay and returned
            when the power bank comes back. They are asked to agree to it in the
            app.
          </p>
        </PowerBankDialog>
      )}

      {step === STEP_RETURN && dialogUnit && (
        <PowerBankDialog
          show
          title="Power bank returned"
          confirmLabel="Mark as returned"
          onConfirm={onReturnConfirm}
          onCancel={closeDialog}
        >
          <p>
            Mark <strong>{unitLabel(dialogUnit)}</strong> as returned by{' '}
            <strong>{dialogAmbassador.name}</strong>?
          </p>
          <p>
            The power bank goes back into city stock and any hold on it is
            released back to the brand ambassador.
          </p>
        </PowerBankDialog>
      )}

      {step === STEP_WRITE_OFF && dialogUnit && (
        <PowerBankDialog
          show
          title="Power bank lost or broken"
          confirmLabel="Write off"
          onConfirm={onWriteOffConfirm}
          onCancel={closeDialog}
        >
          <p>
            Write off <strong>{unitLabel(dialogUnit)}</strong> held by{' '}
            <strong>{dialogAmbassador.name}</strong>?
          </p>
          <p>
            The unit is retired from inventory. Any hold on it is kept by the
            company, and the ambassador can be handed a new power bank.
          </p>
        </PowerBankDialog>
      )}
    </div>
  )
}

PowerBanksSection.propTypes = {
  cityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  availableUnits: PropTypes.arrayOf(
    PropTypes.shape({
      inventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  givenOutUnits: PropTypes.arrayOf(
    PropTypes.shape({
      inventoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      ambassadorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ambassadorName: PropTypes.string,
    }),
  ),
  ambassadors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  ),
  selectedBrandAmbassadors: PropTypes.array,
  updateLocalPowerBankState: PropTypes.func.isRequired,
}

PowerBanksSection.defaultProps = {
  availableUnits: [],
  givenOutUnits: [],
  ambassadors: [],
  selectedBrandAmbassadors: undefined,
}

export default PowerBanksSection
